<template>
    <div class="course-detail-page">
        <NavigationBar />

        <div class="container pt-5 pb-5 content-wrapper">
            <button @click="$router.go(-1)" class="btn btn-link text-secondary text-decoration-none mb-4">
                <i class="bi bi-arrow-left"></i> Volver a los Cursos
            </button>

            <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-danger" role="status"></div>
                <p class="mt-3 text-secondary letter-spacing-2">Descifrando Datos de la Misión...</p>
            </div>

            <div v-else-if="course" class="row g-5">
                <div class="col-lg-8" data-aos="fade-down">
                    <div class="course-hero-img mb-4" :style="{ backgroundImage: `url(${course.image})` }">
                        <div class="category-badge">{{ course.category }}</div>
                    </div>

                    <h1 class="course-title text-white mb-2">{{ course.name }}</h1>
                    <div class="course-meta text-secondary mb-4">
                        <div class="meta-item">
                            <i class="bi bi-geo-alt-fill text-danger me-2"></i>
                            <span>{{ course.fullLocation }}</span>
                        </div>

                        <div class="meta-item">
                            <i class="bi bi-calendar-event-fill text-danger me-2"></i>
                            <span>{{ course.date }}</span>
                        </div>

                        <div class="meta-item">
                            <i class="bi bi-clock-fill text-danger me-2"></i>
                            <span>{{ course.time }}</span>
                        </div>
                    </div>

                    <div class="tactical-tabs mt-5">
                        <ul class="nav nav-tabs border-bottom-0 mb-4">
                            <li class="nav-item">
                                <button class="nav-link" :class="{ active: activeTab === 'overview' }"
                                    @click="activeTab = 'overview'">Descripción General</button>
                            </li>
                            <li class="nav-item">
                                <button class="nav-link" :class="{ active: activeTab === 'gear' }"
                                    @click="activeTab = 'gear'">Lista de Equipo</button>
                            </li>
                        </ul>

                        <div class="tab-content text-gray-300">
                            <div v-if="activeTab === 'overview'">
                                <h4 class="text-white mb-3">Resumen de la Misión</h4>
                                <p>{{ course.missionBrief }}</p>
                                <p class="fw-bold mt-4 text-white small letter-spacing-2">Temas Cubiertos:</p>
                                <ul class="mt-2">
                                    <li v-for="topic in course.topics" :key="topic" class="mb-1">{{ topic }}</li>
                                </ul>
                            </div>
                            <div v-if="activeTab === 'gear'">
                                <h4 class="text-white mb-3">Equipo Requerido</h4>
                                <ul class="gear-list">
                                    <li v-for="item in course.gearRequired" :key="item" class="mb-2">
                                        <i class="bi bi-check2 text-danger me-2"></i> {{ item }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-lg-4" data-aos="fade-up">
                    <div class="purchase-card purchase-sticky">
                        <h3 class="text-white fw-bold mb-4">Asegura tu lugar</h3>

                        <div class="d-flex justify-content-between align-items-center mb-3 text-white">
                            <span>Costo Total del Curso</span>
                            <span class="fs-4 fw-bold text-danger">${{ totalPrice }}</span>
                        </div>

                        <hr class="border-secondary opacity-25 mb-4">

                        <form @submit.prevent="handlePayment">
                            <div class="mb-3">
                                <label class="text-secondary small fw-bold mb-1">Nombre completo</label>
                                <input v-model="form.name" type="text" class="form-control-tactical w-100"
                                    placeholder="Juan Pérez" required>
                            </div>

                            <div class="row g-2 mb-4">
                                <div class="col-md-6">
                                    <label class="text-secondary small fw-bold mb-1">Correo electrónico</label>
                                    <input v-model="form.email" type="email" class="form-control-tactical w-100"
                                        placeholder="correo@ejemplo.com" required>
                                </div>
                                <div class="col-md-6">
                                    <label class="text-secondary small fw-bold mb-1">Teléfono</label>
                                    <input v-model="form.phone" type="tel" class="form-control-tactical w-100"
                                        placeholder="(844) 000-0000" required>
                                </div>
                            </div>

                            <div class="mb-4">
                                <label class="text-secondary small fw-bold mb-2">Operadores</label>
                                <div
                                    class="d-flex align-items-center bg-black border border-secondary border-opacity-50 w-fit">
                                    <button type="button" @click="decrementAttendees"
                                        class="btn btn-sm text-white px-3 fw-bold rounded-0 hover-danger"
                                        :disabled="form.attendees <= 1">−</button>
                                    <div
                                        class="px-3 fw-bold text-white border-start border-end border-secondary border-opacity-50 py-1">
                                        {{ form.attendees }}</div>
                                    <button type="button" @click="incrementAttendees"
                                        class="btn btn-sm text-white px-3 fw-bold rounded-0 hover-danger"
                                        :disabled="form.attendees >= course.maxAttendees">+</button>
                                </div>
                            </div>

                            <div v-if="course.rentalInfo" class="gear-upgrade-box mb-4"
                                :class="{ 'active': form.rental }" @click="form.rental = !form.rental">
                                <div class="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div class="fw-bold text-white small letter-spacing-2">
                                            <i class="bi bi-bullseye me-2"></i>{{ course.rentalInfo.tipo_equipo }}
                                        </div>
                                        <div class="small text-secondary mt-1" style="font-size: 0.75rem;">{{
                                            course.rentalInfo.descripcion }}</div>
                                    </div>
                                    <div class="text-end">
                                        <div class="text-danger fw-bold">+${{ course.rentalInfo.precio_renta }}</div>
                                        <div class="tactical-switch mt-1">
                                            <div class="switch-indicator"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="mb-4">
                                <label class="text-secondary small fw-bold mb-2">Pasarela de Pago Segura</label>
                                <div class="stripe-element-container">
                                    <div id="card-element" ref="cardElementRef"></div>
                                </div>
                                <div id="card-errors" role="alert" class="text-danger small mt-2"></div>
                            </div>

                            <div class="agreements-section mb-4">
                                <label class="custom-checkbox mb-2">
                                    <input type="checkbox" v-model="form.prereq" required>
                                    <span class="checkmark"></span>
                                    <span class="text-label">Certifico que cumplo con los requisitos del curso.</span>
                                </label>
                                <label class="custom-checkbox mb-2">
                                    <input type="checkbox" v-model="form.waiver" required>
                                    <span class="checkmark"></span>
                                    <span class="text-label">Acepto la <a href="#" class="text-danger">Exención de
                                            Responsabilidad</a>.</span>
                                </label>
                                <label class="custom-checkbox">
                                    <input type="checkbox" v-model="form.terms" required>
                                    <span class="checkmark"></span>
                                    <span class="text-label">Acepto la <a href="#" class="text-danger">Política de
                                            Reembolso</a>.</span>
                                </label>
                            </div>

                            <button type="submit" class="btn-purchase w-100" :disabled="processing">
                                <span v-if="!processing">Confirmar y Pagar ${{ totalPrice }}</span>
                                <span v-else><i class="bi bi-arrow-repeat spinner"></i> Procesando...</span>
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            <div v-else class="text-center py-5">
                <i class="bi bi-shield-slash display-1 text-danger opacity-50"></i>
                <h3 class="text-white mt-3">Información del curso no encontrada</h3>
                <button @click="$router.push('/live-training')" class="btn btn-outline-danger mt-3">Volver a la
                    Lista</button>
            </div>
        </div>
        <Footer />
    </div>
</template>

<script setup>
import NavigationBar from "@/components/Layout/NavigationBar.vue";
import Footer from "@/components/Layout/Footer.vue";
import { ref, computed, onMounted, nextTick } from 'vue'; // Añadimos nextTick
import { useRoute, useRouter } from 'vue-router';
import { getCourseDetail, postInscripcion } from "@/utils/api_backend.js";
import AOS from "aos";
import "aos/dist/aos.css";
import { loadStripe } from "@stripe/stripe-js";
import Swal from "sweetalert2"; // Asegúrate de tenerlo instalado

const route = useRoute();
const router = useRouter();
const activeTab = ref('overview');
const processing = ref(false);
const course = ref(null);
const loading = ref(true);

// STRIPE
let stripe = null;
let elements = null;
let cardElement = null; // Quitamos el ref() para manejarlo directamente
const cardElementRef = ref(null);
const stripePublicKey = process.env.VUE_APP_STRIPE_PUBLIC_KEY;

const form = ref({
    name: '',
    email: '',
    phone: '',
    attendees: 1,
    rental: false,
    waiver: false,
    terms: false,
    prereq: false
});

const fetchCourseDetails = async (id) => {
    try {
        loading.value = true;
        const data = await getCourseDetail(id);
        course.value = data; // Aquí Vue activa el v-if

        // Esperamos a que Vue termine de renderizar el nuevo DOM
        await nextTick();

        // Verificamos si el elemento ya existe en el DOM antes de montar
        if (document.getElementById('card-element')) {
            await initStripe();
        } else {
            // Reintento de seguridad en el siguiente ciclo si aún no aparece
            setTimeout(async () => {
                await initStripe();
            }, 50);
        }
    } catch (err) {
        console.error("Critical Failure:", err);
    } finally {
        loading.value = false;
    }
};

const initStripe = async () => {
    // Evitamos duplicar el elemento si ya está montado
    if (cardElement) return;

    if (!stripe) stripe = await loadStripe(stripePublicKey);
    elements = stripe.elements();

    const style = {
        base: {
            color: "#ffffff",
            fontFamily: '"Inter", sans-serif',
            fontSize: "16px",
            "::placeholder": { color: "#666666" }
        },
        invalid: { color: "#ff3131", iconColor: "#ff3131" }
    };

    cardElement = elements.create("card", { style, hidePostalCode: true });
    cardElement.mount("#card-element");
};

onMounted(() => {
    fetchCourseDetails(route.params.id);
    AOS.init();
});

// Lógica de cálculo
const totalPrice = computed(() => {
    if (!course.value) return "0.00";
    let total = course.value.basePrice * form.value.attendees;
    if (form.value.rental && course.value.rentalInfo) {
        total += (parseFloat(course.value.rentalInfo.precio_renta) * form.value.attendees);
    }
    return total.toFixed(2);
});

const handlePayment = async () => {
    if (!form.value.waiver || !form.value.terms || !form.value.prereq) {
        Swal.fire({
            title: "OPERATIONAL ERROR",
            text: "Please accept all mission requirements.",
            icon: "warning",
            background: "#111",
            color: "#fff",
            confirmButtonColor: "#ff3131"
        });
        return;
    }

    processing.value = true;

    try {
        // 1. Crear intención en el Back (Saltillo HQ)
        const payload = {
            curso: course.value.id,
            nombre_completo: form.value.name,
            email: form.value.email,
            telefono: form.value.phone,
            cantidad_asistentes: form.value.attendees,
            incluye_renta: form.value.rental,
            acepta_prerrequisitos: form.value.prereq,
            acepta_deslinde: form.value.waiver,
            acepta_politica_reembolso: form.value.terms
        };

        const { client_secret } = await postInscripcion(payload);

        // 2. Confirmar con Stripe
        const result = await stripe.confirmCardPayment(client_secret, {
            payment_method: {
                card: cardElement,
                billing_details: {
                    name: form.value.name,
                    email: form.value.email
                }
            }
        });

        if (result.error) {
            Swal.fire({
                title: "Payment Failed",
                text: result.error.message || "Your payment could not be processed.",
                icon: "error",
                background: "#111",
                color: "#fff",
                confirmButtonColor: "#ff3131",
                confirmButtonText: "Try Again"
            });
        } else if (result.paymentIntent.status === "succeeded") {
            Swal.fire({
                title: "Payment Confirmed",
                html: `
                    <p>Your enrollment has been successfully completed.</p>
                    <p class="mt-2"><strong>Confirmation details have been sent to your email.</strong></p>
                `,
                icon: "success",
                background: "#111",
                color: "#fff",
                showConfirmButton: false,
                timer: 1500
            }).then(() => {
                router.push('/live-training');
            });
        }
    } catch (error) {
        Swal.fire({
            title: "Connection Error",
            text: "We could not connect to the payment server. Please try again.",
            icon: "error",
            background: "#111",
            color: "#fff",
            confirmButtonColor: "#ff3131"
        });
    } finally {
        processing.value = false;
    }
};

const incrementAttendees = () => {
    if (course.value && form.value.attendees < course.value.maxAttendees) form.value.attendees++;
};
const decrementAttendees = () => {
    if (form.value.attendees > 1) form.value.attendees--;
};
</script>
<style scoped>
/* Los estilos se mantienen exactamente iguales a los que tenías anteriormente */
.course-detail-page {
    background-color: #000;
    min-height: 100vh;
    color: white;
    padding-top: 80px;
}

.course-hero-img {
    width: 100%;
    height: 400px;
    background-size: cover;
    background-position: center;
    border: 1px solid #333;
    position: relative;
}

.category-badge {
    position: absolute;
    bottom: 0;
    left: 0;
    background: #ff3131;
    color: white;
    padding: 10px 20px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 2px;
}

.course-title {
    font-weight: 900;
    font-size: 3rem;
    text-transform: uppercase;
}

.nav-tabs .nav-link {
    background: transparent;
    border: none;
    color: #666;
    font-weight: 800;
    letter-spacing: 1px;
    padding-bottom: 1rem;
    border-bottom: 2px solid transparent;
}

.nav-tabs .nav-link.active {
    background: transparent;
    color: #ff3131;
    border-bottom: 2px solid #ff3131;
}

.purchase-card {
    background: #111;
    border: 1px solid #333;
    padding: 30px;
}

.purchase-sticky {
    position: sticky;
    top: 120px;
    z-index: 10;
}

.form-control-tactical {
    background: #000;
    border: 1px solid #333;
    color: white;
    padding: 12px;
    border-radius: 0;
}

.stripe-mock-input {
    background: #000;
    border: 1px solid #333;
    padding: 12px;
    display: flex;
    align-items: center;
}

.btn-purchase {
    background: #ff3131;
    color: white;
    border: none;
    padding: 15px;
    font-weight: 900;
    text-transform: uppercase;
    transition: 0.3s;
}

.btn-purchase:hover:not(:disabled) {
    background: #ff0000;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(255, 0, 0, 0.3);
}

.gear-upgrade-box {
    background: #1a1a1a;
    border: 1px solid #333;
    padding: 15px;
    cursor: pointer;
    transition: 0.3s;
    border-radius: 4px;
}

.gear-upgrade-box.active {
    background: rgba(255, 49, 49, 0.1);
    border-color: #ff3131;
}

.tactical-switch {
    width: 40px;
    height: 10px;
    background: #333;
    border-radius: 10px;
    position: relative;
}

.switch-indicator {
    width: 16px;
    height: 16px;
    background: #666;
    border-radius: 50%;
    position: absolute;
    top: -3px;
    left: 0;
    transition: 0.3s;
}

.gear-upgrade-box.active .switch-indicator {
    background: white;
    left: 24px;
}

.custom-checkbox {
    display: flex;
    align-items: flex-start;
    cursor: pointer;
    font-size: 0.85rem;
    color: #ccc;
}

.custom-checkbox input {
    position: absolute;
    opacity: 0;
}

.checkmark {
    height: 18px;
    width: 18px;
    background-color: #000;
    border: 1px solid #444;
    margin-right: 10px;
    position: relative;
}

.custom-checkbox input:checked~.checkmark {
    background-color: #ff3131;
    border-color: #ff3131;
}

.checkmark:after {
    content: "";
    position: absolute;
    display: none;
    left: 6px;
    top: 2px;
    width: 5px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
}

.custom-checkbox input:checked~.checkmark:after {
    display: block;
}

.spinner {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    100% {
        transform: rotate(360deg);
    }
}

.letter-spacing-2 {
    letter-spacing: 2px;
}

.w-fit {
    width: fit-content;
}

.stripe-element-container {
    background: #000;
    border: 1px solid #333;
    padding: 15px;
    transition: border-color 0.3s ease;
}

.stripe-element-container:focus-within {
    border-color: #ff3131;
    box-shadow: 0 0 10px rgba(255, 49, 49, 0.2);
}

.course-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
}

.meta-item {
    display: flex;
    align-items: center;
}

/* 📱 Mobile */
@media (max-width: 768px) {
    .course-meta {
        flex-direction: column;
        gap: 10px;
    }
}
</style>